/**
 * Club Set Definitions and Utilities
 * 
 * Handles iron and wedge set configurations matching how golfers
 * actually buy and think about their equipment.
 */

import { GolfClub, ClubType } from '../types';

export interface ClubSet {
  id: string;
  name: string;
  clubs: ClubType[];
  description: string;
  category: 'irons' | 'wedges';
}

/**
 * Standard Iron Set Configurations
 */
export const IRON_SETS: ClubSet[] = [
  {
    id: '3-pw',
    name: '3-PW',
    clubs: ['3-iron', '4-iron', '5-iron', '6-iron', '7-iron', '8-iron', '9-iron', 'pw'],
    description: '8 irons (3-PW)',
    category: 'irons'
  },
  {
    id: '4-pw',
    name: '4-PW',
    clubs: ['4-iron', '5-iron', '6-iron', '7-iron', '8-iron', '9-iron', 'pw'],
    description: '7 irons (4-PW)',
    category: 'irons'
  },
  {
    id: '5-pw',
    name: '5-PW',
    clubs: ['5-iron', '6-iron', '7-iron', '8-iron', '9-iron', 'pw'],
    description: '6 irons (5-PW)',
    category: 'irons'
  },
  {
    id: '6-pw',
    name: '6-PW',
    clubs: ['6-iron', '7-iron', '8-iron', '9-iron', 'pw'],
    description: '5 irons (6-PW)',
    category: 'irons'
  },
  {
    id: '4-pw-aw',
    name: '4-PW,AW',
    clubs: ['4-iron', '5-iron', '6-iron', '7-iron', '8-iron', '9-iron', 'pw', 'aw'],
    description: '8 irons (4-PW + AW)',
    category: 'irons'
  },
  {
    id: '5-pw-aw',
    name: '5-PW,AW',
    clubs: ['5-iron', '6-iron', '7-iron', '8-iron', '9-iron', 'pw', 'aw'],
    description: '7 irons (5-PW + AW)',
    category: 'irons'
  },
  {
    id: '6-pw-aw',
    name: '6-PW,AW',
    clubs: ['6-iron', '7-iron', '8-iron', '9-iron', 'pw', 'aw'],
    description: '6 irons (6-PW + AW)',
    category: 'irons'
  },
];

/**
 * Standard Wedge Set Configurations
 */
export const WEDGE_SETS: ClubSet[] = [
  {
    id: 'pw-aw-sw',
    name: 'PW,AW,SW',
    clubs: ['pw', 'aw', 'sw'],
    description: '3 wedges (PW, AW, SW)',
    category: 'wedges'
  },
  {
    id: 'pw-gw-sw-lw',
    name: 'PW,GW,SW,LW',
    clubs: ['pw', 'gw', 'sw', 'lw'],
    description: '4 wedges (full setup)',
    category: 'wedges'
  },
  {
    id: 'aw-sw',
    name: 'AW,SW',
    clubs: ['aw', 'sw'],
    description: '2 wedges (AW, SW)',
    category: 'wedges'
  },
  {
    id: 'sw-lw',
    name: 'SW,LW',
    clubs: ['sw', 'lw'],
    description: '2 wedges (SW, LW)',
    category: 'wedges'
  },
  {
    id: '50-54-58',
    name: '50°/54°/58°',
    clubs: ['gw', 'sw', 'lw'],
    description: '3 wedges (50°, 54°, 58°)',
    category: 'wedges'
  },
  {
    id: '52-56-60',
    name: '52°/56°/60°',
    clubs: ['gw', 'sw', 'lw'],
    description: '3 wedges (52°, 56°, 60°)',
    category: 'wedges'
  },
];

/**
 * Auto-detect and group existing individual clubs into sets
 */
export function autoGroupClubsIntoSets(clubs: GolfClub[]): GolfClub[] {
  const clubsCopy = [...clubs];
  const ironTypes = ['3-iron', '4-iron', '5-iron', '6-iron', '7-iron', '8-iron', '9-iron', 'pw', 'aw', 'gw'];
  const wedgeTypes = ['pw', 'aw', 'gw', 'sw', 'lw'];
  
  // Find consecutive irons
  const irons = clubsCopy.filter(c => ironTypes.includes(c.type));
  if (irons.length >= 3) {
    const ironSetId = `iron-set-${Date.now()}`;
    const firstIronType = irons[0].type;
    const lastIronType = irons[irons.length - 1].type;
    
    irons.forEach((club, index) => {
      club.setId = ironSetId;
      club.isSetParent = index === 0;
      club.setName = `${firstIronType.replace('-iron', '').toUpperCase()}-${lastIronType.toUpperCase()} Set`;
    });
  }
  
  // Find wedges (but exclude any already in iron set)
  const wedges = clubsCopy.filter(c => 
    wedgeTypes.includes(c.type) && !c.setId
  );
  
  if (wedges.length >= 2) {
    const wedgeSetId = `wedge-set-${Date.now()}`;
    const wedgeNames = wedges.map(w => w.type.toUpperCase()).join(',');
    
    wedges.forEach((club, index) => {
      club.setId = wedgeSetId;
      club.isSetParent = index === 0;
      club.setName = `${wedgeNames} Wedge Set`;
    });
  }
  
  return clubsCopy;
}

/**
 * Get all clubs in a set
 */
export function getClubsInSet(clubs: GolfClub[], setId: string): GolfClub[] {
  return clubs.filter(c => c.setId === setId);
}

/**
 * Get set parent club (representative for display)
 */
export function getSetParent(clubs: GolfClub[], setId: string): GolfClub | undefined {
  return clubs.find(c => c.setId === setId && c.isSetParent);
}

/**
 * Check if club is part of a set
 */
export function isInSet(club: GolfClub): boolean {
  return !!club.setId;
}

/**
 * Create clubs from set selection (for onboarding)
 */
export function createClubsFromSet(
  setConfig: ClubSet,
  baseSpecs: {
    brand?: string;
    model?: string;
    shaftType?: string;
    shaftFlex?: string;
    gripType?: string;
  } = {}
): GolfClub[] {
  const setId = `${setConfig.id}-${Date.now()}`;
  
  return setConfig.clubs.map((clubType, index) => ({
    id: `${clubType}-${Date.now()}-${index}`,
    type: clubType,
    brand: baseSpecs.brand || '',
    model: baseSpecs.model || '',
    shaftType: (baseSpecs.shaftType || (clubType.includes('iron') || clubType === 'pw' || clubType === 'aw' || clubType === 'gw' ? 'steel' : 'steel')) as "graphite" | "steel",
    shaftFlex: (baseSpecs.shaftFlex || 'regular') as "extra-stiff" | "stiff" | "regular" | "senior" | "ladies",
    lie: 60,
    loft: getDefaultLoft(clubType),
    length: 36,
    isInBag: true,
    distance: undefined,
    isDistanceEstimated: true,
    gripType: (baseSpecs.gripType || 'standard') as "standard" | "midsize" | "jumbo" | "undersize" | "corded" | "wrap",
    setId,
    isSetParent: index === 0,
    setName: setConfig.name,
    notes: `Part of ${setConfig.name} set`
  })) as GolfClub[];
}

/**
 * Get default loft for club type
 */
function getDefaultLoft(clubType: ClubType): number {
  const loftMap: Record<string, number> = {
    '3-iron': 21,
    '4-iron': 24,
    '5-iron': 27,
    '6-iron': 30,
    '7-iron': 34,
    '8-iron': 38,
    '9-iron': 42,
    'pw': 46,
    'aw': 50,
    'gw': 52,
    'sw': 56,
    'lw': 60,
  };
  return loftMap[clubType] || 30;
}

/**
 * Update all clubs in a set with new specs
 */
export function updateSetSpecs(
  clubs: GolfClub[],
  setId: string,
  updates: Partial<GolfClub>
): GolfClub[] {
  return clubs.map(club => {
    if (club.setId === setId) {
      return {
        ...club,
        ...updates,
        // Preserve individual fields that shouldn't be overwritten
        id: club.id,
        type: club.type,
        loft: club.loft, // Keep individual lofts
        distance: club.distance, // Keep individual distances
        isDistanceEstimated: club.isDistanceEstimated,
        setId: club.setId,
        isSetParent: club.isSetParent,
        setName: club.setName,
      };
    }
    return club;
  });
}

/**
 * Get display name for a set
 */
export function getSetDisplayName(clubs: GolfClub[], setId: string): string {
  const setClubs = getClubsInSet(clubs, setId);
  if (setClubs.length === 0) return 'Unknown Set';
  
  const parent = getSetParent(clubs, setId);
  if (parent?.setName) return parent.setName;
  
  // Fallback: generate from club types
  const firstClub = setClubs[0].type;
  const lastClub = setClubs[setClubs.length - 1].type;
  return `${firstClub.toUpperCase()}-${lastClub.toUpperCase()} Set`;
}

